export const PATIENT_TRACKING_HUB_PATH = '/hubs/patient-tracking';

export const PATIENT_TRACKING_API_BASE = '/api/patient-tracking';

/** Group-scoped Client Portal PharmCollab tracker API. */
export const PATIENT_TRACKING_SCOPE_API_BASE = '/api/patient-tracking/scope';

/** Board keys mirror the server PatientTrackingBoards constants (presence + change groups). */
export const PatientTrackingBoardKeys = {
  /** Master patient records (Patients page). */
  patients: 'patients',
  /** Patient communication tracker (issue rows). */
  tracker: 'tracker',
  coveredEntities: 'covered-entities',
  alertingHistory: 'alerting-history'
} as const;

export type PatientTrackingBoardKey =
  (typeof PatientTrackingBoardKeys)[keyof typeof PatientTrackingBoardKeys];

/** Documented patient statuses (UI shows these names; server stores stable numeric codes). */
export const PATIENT_STATUSES = [
  'Pending',
  'FollowUp',
  'OnHold',
  'Reviewed',
  'Sent',
  'Lost',
  'Resolved'
] as const;

export type PatientStatus = (typeof PATIENT_STATUSES)[number];

export const PATIENT_STATUS_LABELS: Record<PatientStatus, string> = {
  Pending: 'Pending',
  FollowUp: 'Follow Up',
  OnHold: 'On Hold',
  Reviewed: 'Reviewed',
  Sent: 'Sent',
  Lost: 'Lost',
  Resolved: 'Resolved'
};

export const PATIENT_STATUS_OPTIONS = PATIENT_STATUSES.map((value) => ({
  value,
  label: PATIENT_STATUS_LABELS[value]
}));

export const CE_STATUSES = ['Active', 'Inactive', 'Pending'] as const;
export type CeStatus = (typeof CE_STATUSES)[number];
export const CE_STATUS_OPTIONS = CE_STATUSES.map((value) => ({ value, label: value }));

export const ALERT_RESULTS = ['Success', 'Trouble'] as const;
export type AlertResult = (typeof ALERT_RESULTS)[number];

// ---- Our pharmacies ---------------------------------------------------------

export interface OurPharmacyDto {
  id: string;
  pharmacyName: string;
  legalName: string | null;
  npi: string | null;
  nabp: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  mainPhone: string | null;
  fax: string | null;
  email: string | null;
  website: string | null;
  primaryContactName: string | null;
  primaryContactTitle: string | null;
  primaryContactEmail: string | null;
  primaryContactPhone: string | null;
  notes: string | null;
  isActive: boolean;
  created: string;
  createdBy: string | null;
  modified: string;
  modifiedBy: string | null;
}

export interface OurPharmacyUpsertBody {
  pharmacyName: string;
  legalName: string | null;
  npi: string | null;
  nabp: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  mainPhone: string | null;
  fax: string | null;
  email: string | null;
  website: string | null;
  primaryContactName: string | null;
  primaryContactTitle: string | null;
  primaryContactEmail: string | null;
  primaryContactPhone: string | null;
  notes: string | null;
  isActive: boolean;
}

// ---- Patient master ---------------------------------------------------------

export interface PatientDto {
  id: string;
  pharmacyId: string;
  pharmacyName: string | null;
  firstName: string;
  lastName: string;
  /** Convenience "Last, First" computed by the server. */
  fullName: string;
  /** ISO date string (YYYY-MM-DD) or null when DOB is not on file. */
  dob: string | null;
  phone: string | null;
  email: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  homeCoveredEntityId: string | null;
  homeCoveredEntityName: string | null;
  enrollmentForm: boolean;
  notes: string | null;
  isActive: boolean;
  /** Open tracker issues (excludes Resolved and Lost). */
  openIssues: number;
  /** Total tracker issues (all statuses). */
  totalIssues: number;
  created: string;
  createdBy: string | null;
  modified: string;
  modifiedBy: string | null;
}

export interface PatientUpsertBody {
  pharmacyId: string;
  firstName: string;
  lastName: string;
  dob: string | null;
  phone: string | null;
  email: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  homeCoveredEntityId: string | null;
  enrollmentForm: boolean;
  notes: string | null;
  isActive: boolean;
  /**
   * When true and the server detects possible duplicate patients, the save is rejected with the
   * candidates returned in the error payload so the UI can prompt "use existing".
   */
  rejectIfDuplicate?: boolean;
}

export interface PatientDuplicateMatch {
  id: string;
  fullName: string;
  dob: string | null;
  homeCoveredEntityName: string | null;
  openIssues: number;
}

export interface PatientSaveResult {
  patient: PatientDto;
  possibleDuplicates: PatientDuplicateMatch[];
}

// ---- Tracker entries (issues) ----------------------------------------------

export interface TrackerEntryDto {
  id: string;
  pharmacyId: string;
  pharmacyName: string | null;
  status: PatientStatus;
  patientId: string;
  /** Read-only mirror of Patient.fullName for grid display. */
  patientName: string;
  /** Read-only mirror of Patient.dob (ISO date) for grid display. */
  patientDob: string | null;
  /** Read-only mirror of Patient.enrollmentForm. Edits go through the tracker upsert (write-through). */
  enrollmentForm: boolean;
  coveredEntityId: string;
  coveredEntityName: string | null;
  currentIssue: string;
  changeLog: string | null;
  internalNote: string | null;
  emailsSent: number;
  emailLastSent: string | null;
  nextAlertDate: string | null;
  created: string;
  createdBy: string | null;
  modified: string;
  modifiedBy: string | null;
}

export interface TrackerEntryUpsertBody {
  pharmacyId: string;
  status: PatientStatus;
  patientId: string;
  /** Null = use the patient's home CE on the server. */
  coveredEntityId: string | null;
  currentIssue: string;
  /** Null = no change to the patient's enrollment flag; non-null writes through to the Patient. */
  enrollmentForm: boolean | null;
  changeLog: string | null;
  internalNote: string | null;
}

export interface CoveredEntityDto {
  id: string;
  pharmacyId: string;
  pharmacyName: string | null;
  ceStatus: CeStatus;
  coveredEntityName: string;
  ceCodes: string | null;
  portalGroupName: string | null;
  sendAlertsToEmail: string | null;
  ccAlertsToEmail: string | null;
  ceOptOut: boolean;
  ceGeneralNote: string | null;
  prevDaySent: number;
  lastAlertSent: string | null;
  totalAlertsSent: number;
  totalRecords: number;
  pendingCount: number;
  followUpCount: number;
  onHoldCount: number;
  reviewedCount: number;
  sentCount: number;
  lostCount: number;
  resolvedCount: number;
  created: string;
  createdBy: string | null;
  modified: string;
  modifiedBy: string | null;
}

export interface CoveredEntityUpsertBody {
  pharmacyId: string;
  ceStatus: CeStatus;
  coveredEntityName: string;
  ceCodes: string | null;
  portalGroupName: string | null;
  sendAlertsToEmail: string | null;
  ccAlertsToEmail: string | null;
  ceOptOut: boolean;
  ceGeneralNote: string | null;
}

export interface AlertingHistoryDto {
  id: string;
  pharmacyId: string;
  pharmacyName: string | null;
  sentDate: string;
  result: AlertResult;
  summary: string | null;
  totalRecords: number;
  patientAlerts: number;
  ceAlerted: number;
  emailsSent: number;
  errors: number;
  sentToIds: string | null;
  errorIds: string | null;
  created: string;
  createdBy: string | null;
  modified: string;
  modifiedBy: string | null;
}

export interface AlertingHistoryUpsertBody {
  pharmacyId: string;
  sentDate: string;
  result: AlertResult;
  summary: string | null;
  totalRecords: number;
  patientAlerts: number;
  ceAlerted: number;
  emailsSent: number;
  errors: number;
  sentToIds: string | null;
  errorIds: string | null;
}

export interface PatientTrackingImportRowError {
  rowNumber: number;
  field: string;
  message: string;
}

export interface PatientTrackingImportResult {
  inserted: number;
  skipped: number;
  errors: PatientTrackingImportRowError[];
}

export interface PatientTrackingCsvImportToasts {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
}

export interface PatientTrackingCsvImportDefinition {
  title: string;
  /** Short guidance shown at the top of the import dialog. */
  intro?: string;
  templateUrl: string;
  templateFileName: string;
  importCsv: (file: File) => Promise<PatientTrackingImportResult>;
  /** Optional authenticated template download (preferred over plain templateUrl link). */
  downloadTemplate?: () => Promise<void>;
}

export interface PatientTrackingCsvImportConfig extends PatientTrackingCsvImportDefinition {
  toasts?: PatientTrackingCsvImportToasts;
}

/** One active user on a tracker board (presence roster row). */
export interface PatientRosterParticipant {
  userId: string;
  displayName: string;
  upn: string | null;
  connections: number;
  editingRowId: string | null;
  listEditing: boolean;
}

export interface PatientTrackerFieldPolicy {
  hidePharmacy?: boolean;
  hideCoveredEntity?: boolean;
  hideInternalNote?: boolean;
  readOnlyPatient?: boolean;
  allowCreate?: boolean;
  allowDelete?: boolean;
}

export const CLIENT_PORTAL_PHARMCOLLAB_FIELD_POLICY: PatientTrackerFieldPolicy = {
  hidePharmacy: true,
  hideCoveredEntity: true,
  hideInternalNote: true,
  readOnlyPatient: true,
  allowCreate: false,
  allowDelete: false
};

/** Row-change push payload (RowUpserted / RowRemoved). */
export interface PatientTrackingRowChange<TRow = unknown> {
  boardKey: string;
  changeType: string;
  row: TRow | null;
  rowId: string | null;
  changedByUserId: string | null;
  changedByDisplayName: string | null;
  changedAtUtc: string;
}
